import {observer_mgl} from "mobx-graphlink";
import React, {useEffect, useMemo, useRef} from "react";
import * as THREE from "three";
import {GetDisplayTitleForObj, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {LabelPlacement, MapObject, ShapeType} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {Html} from "@react-three/drei"
import {AdaptiveLabel, AdaptiveLabel_SizeInfo} from "./MapObjectUI/AdaptiveLabel.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {MeshUI} from "./MapObjectUI/MeshUI.js";
import {InfoButton} from "web-vcore";
import {mapZIndexes} from "./MapZIndexes.js";
import {PathUI} from "./MapObjectUI/PathUI.js";
import {PathFillUI} from "./MapObjectUI/PathFillUI.js";
import {GetJournalEntry} from "../../../../Server/DB/journalEntries.js";
import {Rect, Vec2, Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {useBase64ImageStrAsTexture} from "../../../../Utils/ThreeJS/3JSHooks.js";
import {GetPointsMatchingObjShape} from "../ObjectComps/Meshes_CompUI.js";
import {GeoBounds, ValidateTileRequest} from "./RLMapTiles/@Shared.js";
import {TILE_MAX_TILE_COUNT_FOR_REGION, TILE_MAX_REGION_LENGTH_KM, TileGrid_OSM} from "./RLMapTiles/OSM.js";
import {ColorArrayToTHREEColor} from "../../../../Utils/ThreeJS/ColorUtils.js";
import {AnchorEntityMapIcons} from "./MapObjectUI/AnchorEntityMapIcon.js";
import {ObjMesh} from "../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {attachRenderTiming, detachRenderTiming} from "../../../../Utils/ThreeJS/MapObjectRenderStats.js";
import {ErrorBoundaryR3} from "../../../../Utils/ReactComponents/ErrorBoundaryR3.js";
import {BuildingPlanOverlays2D} from "./Building/PlanOverlays2D.js";
import {ObjHasRenderableBuilding} from "./MapObject3D.js";

export const GetObjLabelSizeInfo = (
	obj: MapObject,
	fallback = {
		width: 1,
		minPixelWidth: obj.label_minWidth_screen ?? 0, maxPixelWidth: obj.label_maxWidth_screen ?? 0,
		minPixelHeight: obj.label_minHeight_screen ?? 0, maxPixelHeight: obj.label_maxHeight_screen ?? 0,
	} as AdaptiveLabel_SizeInfo
): AdaptiveLabel_SizeInfo=>{
	const screenConstraints = {
		minPixelWidth: obj.label_minWidth_screen ?? 0, maxPixelWidth: obj.label_maxWidth_screen ?? 0,
		minPixelHeight: obj.label_minHeight_screen ?? 0, maxPixelHeight: obj.label_maxHeight_screen ?? 0,
	};
	if (obj.label_baseFontHeight_world) return {height_for1Line: obj.label_baseFontHeight_world, ...screenConstraints};
	if (obj.label_baseWidth_world)      return {width: obj.label_baseWidth_world, ...screenConstraints};
	if (obj.shape_size?.x)              return {width: obj.shape_size.x, ...screenConstraints};
	return fallback;
};

const MapObjectLabel = observer_mgl_r3(({obj, mapState}: {obj: MapObject, mapState: MapState})=>{
	const title = GetDisplayTitleForObj(obj, "mapSurface");

	// in screenshot-mode, hide any label whose text is derived from a dream-title (currently the case, if obj name is "@" AND no label-override is set)
	if (obj.name == "@" && !obj.label_textOverride && mapState.settings.general.screenshotMode) return null;
	return (
		<AdaptiveLabel mapState={mapState} position={Vec3.zero} offset={obj.label_offset ?? Vec2.zero} placement={obj.label_placement ?? LabelPlacement.center} sizeInfo={GetObjLabelSizeInfo(obj)} text={title}/>
	);
});

export const BasicShape = observer_mgl_r3((props: {localPos: Vec3, type: ShapeType, size: Vec2, rounding: number, lineOnly: boolean, color: string|number|THREE.Color, opacity?: number})=>{
	const {localPos, type, size, rounding, lineOnly, color, opacity = 1} = props;

	const ellipseCurve_points_xAndYs = useMemo(()=>{
		if (type != ShapeType.ellipse) return new Float32Array();
		const radiusX = (size.x ?? 0) / 2 || 1;
		const radiusY = (size.y ?? 0) / 2 || 1;
		const segments = 64;
		const ellipseGeometry = new THREE.EllipseCurve(0, 0, radiusX, radiusY, 0, Math.PI * 2, false, 0);
		return new Float32Array(ellipseGeometry.getPoints(segments).flatMap(a=>[a.x, a.y, 0]));
	}, [type, size]);

	const rectCurve_points_xAndYs = useMemo(()=>{
		if (type != ShapeType.rectangle) return new Float32Array();
		const width = size.x ?? 0;
		const height = size.y ?? 0;
		const halfWidth = width / 2;
		const halfHeight = height / 2;

		// Clamp rounding to not exceed half of the smallest dimension
		const maxRounding = Math.min(halfWidth, halfHeight);
		const actualRounding = Math.min(rounding, maxRounding);

		if (actualRounding <= 0) {
			// No rounding, use simple rectangle
			const points = [
				new THREE.Vector3(-halfWidth, -halfHeight, 0),
				new THREE.Vector3(halfWidth, -halfHeight, 0),
				new THREE.Vector3(halfWidth, halfHeight, 0),
				new THREE.Vector3(-halfWidth, halfHeight, 0),
				new THREE.Vector3(-halfWidth, -halfHeight, 0)
			];
			return new Float32Array(points.flatMap(a=>[a.x, a.y, a.z]));
		}

		// Create rounded rectangle with arcs at corners
		const points: THREE.Vector3[] = [];
		const segments = 16; // segments per corner arc

		// Helper to add arc points
		const addArc = (centerX: number, centerY: number, startAngle: number)=>{
			for (let i = 0; i <= segments; i++) {
				const angle = startAngle + (i / segments) * (Math.PI / 2);
				const x = centerX + actualRounding * Math.cos(angle);
				const y = centerY + actualRounding * Math.sin(angle);
				points.push(new THREE.Vector3(x, y, 0));
			}
		};

		// Bottom-right corner (start at 270°/-90°)
		addArc(halfWidth - actualRounding, -halfHeight + actualRounding, -Math.PI / 2);
		// Top-right corner (start at 0°)
		addArc(halfWidth - actualRounding, halfHeight - actualRounding, 0);
		// Top-left corner (start at 90°)
		addArc(-halfWidth + actualRounding, halfHeight - actualRounding, Math.PI / 2);
		// Bottom-left corner (start at 180°)
		addArc(-halfWidth + actualRounding, -halfHeight + actualRounding, Math.PI);

		// Close the shape by connecting back to start
		if (points.length > 0) {
			points.push(points[0].clone());
		}

		return new Float32Array(points.flatMap(a=>[a.x, a.y, a.z]));
	}, [type, size, rounding]);

	return (
		<mesh position={localPos.To3JPos()}>
			{lineOnly && <line>
				<bufferGeometry>
					{type == ShapeType.ellipse && <bufferAttribute attach="attributes-position" args={[ellipseCurve_points_xAndYs, 3]}/>}
					{type == ShapeType.rectangle && <bufferAttribute attach="attributes-position" args={[rectCurve_points_xAndYs, 3]}/>}
				</bufferGeometry>
				<lineBasicMaterial color={color}
					// commented; react-three-fiber seems to not reliably support toggling transparent true/false after mount
					//transparent={opacity < 1}
					transparent={true}
					opacity={opacity}/>
			</line>}
			{/*!shapeUsesLine && <>
				<meshBasicMaterial color={selected ? "rgba(255,128,0,1)" : 0xffff00} wireframe={true}/>
			</>*/}
		</mesh>
	);
});

export const MapObjectUI = observer_mgl_r3((props: {obj: MapObject, map: DMap, mapState: MapState})=>{
	const {obj, map, mapState} = props;
	const selected = mapState.selectedObjects.includes(obj.id);
	const objPos_world = ResolveObjPosInWorld(obj);
	const objRot_world = ResolveObjRotInWorld(obj);

	// Timing refs — childadded listeners auto-track new renderables, so [] deps is sufficient.
	const groupRef = useRef<THREE.Group>(null);
	useEffect(()=>{
		const group = groupRef.current;
		if (group) attachRenderTiming(group, obj.id);
		return ()=>{ if (group) detachRenderTiming(group); };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const shapeUsesLine = useMemo(()=>{
		switch (obj.shape_type) {
			case ShapeType.ellipse: return true;
			case ShapeType.rectangle: return true;
			default: return false;
		}
	}, [obj.shape_type]);

	//const shapeImageAsTexture = useBase64ImageStrAsTexture(obj.shape_image_embedded);
	const meshForShapeImage = useMemo(()=>{
		if (!obj.shape_image_embedded) return null;
		const size = obj.shape_size;
		if (size == null) return null;
		if (obj.shape_type == null) return null;
		/*const geometry = new THREE.PlaneGeometry(obj.shape_size?.x ?? 1, obj.shape_size?.y ?? 1);
		const material = new THREE.MeshBasicMaterial({
			map: shapeImageAsTexture,
			transparent: true,
			opacity: obj.shape_image_opacity ?? 1,
			side: THREE.DoubleSide // render both sides of the plane
		});
		return <mesh geometry={geometry} material={material} position={Vec3.zero.To3JPos()}/>;*/
		return new ObjMesh({
			points: GetPointsMatchingObjShape(obj.shape_size, obj.shape_type),
			texture_size: size,
			opacity: obj.shape_image_opacity ?? 1,
		});
	}, [obj]);

	const objRot_world_asRadians = objRot_world * (Math.PI / 180);

	// Calculate outline color and opacity
	const outlineColorArray = useMemo(()=>{
		const defaultColorForState = selected ? [255, 128, 0] : [255, 255, 0]; // default: orange when selected, yellow otherwise
		return obj.shape_outlineColor ?? defaultColorForState;
	}, [obj.shape_outlineColor, selected]);
	const outlineColorAlpha = outlineColorArray.length >= 4 ? outlineColorArray[3] : 1;
	const outlineOpacity = outlineColorAlpha * (obj.shape_image_opacity ?? 1);
	const outlineColor = useMemo(()=>ColorArrayToTHREEColor(outlineColorArray, false), [outlineColorArray]);

	return <>
		<group ref={groupRef} position={objPos_world.To3JPos()}>
			{obj.label_show && mapState.settings.general.showLabels && <MapObjectLabel obj={obj} mapState={mapState}/>}
			<AnchorEntityMapIcons {...{obj, map, mapState}}/>
			<RotationGroup rotation={-objRot_world_asRadians}>
				{mapState.settings.general.showShapes && <BasicShape localPos={Vec3.zero.NewZ(mapZIndexes.objShape)}
					type={obj.shape_type ?? ShapeType.rectangle} size={obj.shape_size ?? new Vec2(0, 0)} rounding={obj.shape_rounding ?? 0}
					lineOnly={shapeUsesLine} color={outlineColor} opacity={outlineOpacity}/>
				}
				{meshForShapeImage && <MeshUI {...{obj, map, mapState}} mesh={meshForShapeImage} textureOverride={obj.shape_image_embedded} uvsOverride_asPointBounds={true}/>}
				{(obj.meshes ?? []).map((mesh, i)=>{
					if (mesh.renderDisabled) return null;
					return (
						<ErrorBoundaryR3 key={i} position={objPos_world} message="Error rendering mesh">
							<MeshUI {...{mesh, obj, map, mapState}}/>
						</ErrorBoundaryR3>
					);
				})}
				{obj.shape_osm_enabled && obj.shape_osm_region && IsRLTileGridRegionValid(obj.shape_osm_region, obj.shape_osm_sizeAdjust ?? 1, obj.shape_osm_zoom_base ?? 0) &&
					<TileGrid_OSM mapState={mapState} containerObjPos={objPos_world} focusLevel={0}
						bounds={RectToGeoBounds(obj.shape_osm_region, obj.shape_osm_sizeAdjust ?? 1)} zoom={obj.shape_osm_zoom_base ?? 0} opacity={obj.shape_image_opacity ?? 1}
						shapeType={obj.shape_type} shapeSize={obj.shape_size} restrictToShapeBounds={obj.shape_osm_restrictToShapeBounds ?? false}/>
				}
				{obj.shape_osm_enabled && obj.shape_osm_region && mapState.settings.general.focusL1_enabled && IsRLTileGridRegionValid(obj.shape_osm_region, obj.shape_osm_sizeAdjust ?? 1, obj.shape_osm_zoom_focusL1 ?? 0) &&
					<TileGrid_OSM mapState={mapState} containerObjPos={objPos_world} focusLevel={1}
						bounds={RectToGeoBounds(obj.shape_osm_region, obj.shape_osm_sizeAdjust ?? 1)} zoom={obj.shape_osm_zoom_focusL1 ?? 0} opacity={obj.shape_image_opacity ?? 1}
						shapeType={obj.shape_type} shapeSize={obj.shape_size} restrictToShapeBounds={obj.shape_osm_restrictToShapeBounds ?? false}/>
				}
				{(obj.paths ?? []).map((path, i)=>{
					const fill = path.fill;
					return <React.Fragment key={i}>
						{!path.renderDisabled &&
						<ErrorBoundaryR3 position={objPos_world} message="Error rendering path">
							<PathUI {...{path, obj, map, mapState}}/>
						</ErrorBoundaryR3>}
						{fill != null && !fill.renderDisabled &&
						<ErrorBoundaryR3 position={objPos_world} message="Error rendering path">
							<PathFillUI {...{fill, path, obj, map, mapState}}/>
						</ErrorBoundaryR3>}
					</React.Fragment>;
				})}
				{/* Buildings Set1 §6.3 — 2D plan overlays (wall outlines, openings, roof, active floor). */}
				{ObjHasRenderableBuilding(obj) && <BuildingPlanOverlays2D obj={obj} mapState={mapState}/>}
			</RotationGroup>
		</group>
	</>;
});

/** If rotation is non-zero, wraps children in a <group> with the given Z rotation.
 * Else, wraps children in a plain fragment. (to minimize threejs group nesting) */
const RotationGroup = ({rotation, children}: {rotation: number, children: React.ReactNode})=>{
	if (rotation != 0) return <group rotation={[0, 0, rotation]}>{children}</group>;
	return <>{children}</>;
};

export function IsRLTileGridRegionValid(region: Rect|n, sizeAdjust: number, zoomLevel: number) {
	if (region == null) return false;
	if (region.x == null || region.y == null || region.width == null || region.height == null) return false;
	if (region.width <= 0 || region.height <= 0) return false;

	// to ensure we don't request a crazy amount of tiles, also ensure that:
	// * Total-area is <= the area of a 2km x 2km square
	// * Total tile-count is <= 100 
	try {
		ValidateTileRequest(RectToGeoBounds(region, sizeAdjust), zoomLevel, TILE_MAX_REGION_LENGTH_KM, TILE_MAX_TILE_COUNT_FOR_REGION);
	} catch (ex) {
		console.error("Invalid tile request:", ex?.["message"] ?? ex);
		return false;
	}

	return true;
}
export function RectToGeoBounds(rect: Rect, sizeAdjust: number): GeoBounds {
	// ensure the rect is an actual instanceof Rect first (not just a plain object), so that the getters work
	const rect_rich = new Rect(rect.x, rect.y, rect.width, rect.height, true);
	const rect_final = sizeAdjust != null && sizeAdjust != 1
		? Rect.FromCenter(rect_rich.Center, rect_rich.Size.Times(sizeAdjust), true)
		: rect_rich;
	return {
		north: rect_final.Top,
		south: rect_final.Bottom,
		east: rect_final.Right,
		west: rect_final.Left
	};
}